import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { generateReport, exportReport, getReportRuns } from "../../api/axios";
import { clampPct } from "../../lib/utils";
import { downloadBlob, generatePdfFromReportData } from "../../lib/reportUtils";
import { useToast } from "../../hooks/use-toast";
import { 
  Sparkles, 
  RefreshCw, 
  Plus, 
  AlertCircle, 
  FileText, 
  CheckCircle2, 
  Clock, 
  Brain, 
  Loader2, 
  Eye, 
  Download, 
  File, 
  Lightbulb 
} from "lucide-react";

// AI report types only - filter out availability, performance, capacity-planning, sla-compliance, incident
const AI_REPORT_TYPES = ["ai-analysis", "anomaly-detection", "predictive-analytics"];

export default function AIReports() {
  const { toast } = useToast();
  const [selectedReportType, setSelectedReportType] = useState<"all" | "comprehensive" | "anomaly" | "predictive">("all");
  const [selectedTimeRange, setSelectedTimeRange] = useState("7d");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Convert frontend time range to days
  const timeRangeToDays = (range: string): number => {
    switch (range) {
      case "24h": return 1;
      case "7d": return 7;
      case "30d": return 30;
      case "90d": return 90;
      default: return 7;
    }
  };

  // Fetch report runs from backend
  const { data: reportRunsData, isLoading: isLoadingRuns, error: runsError, refetch: refetchRuns } = useQuery({
    queryKey: ["/api/reports/runs", selectedReportType, selectedTimeRange],
    queryFn: async () => {
      const days = timeRangeToDays(selectedTimeRange);
      
      // When "all" is selected, fetch all AI report types by making separate queries
      // and combining them, since backend only accepts one type at a time
      if (selectedReportType === "all") {
        const [aiAnalysis, anomaly, predictive] = await Promise.all([
          getReportRuns({ days, type: "ai-analysis", limit: 50 }),
          getReportRuns({ days, type: "anomaly-detection", limit: 50 }),
          getReportRuns({ days, type: "predictive-analytics", limit: 50 }),
        ]);
        
        // Combine all runs and sort by createdAt descending
        const allRuns = [
          ...(aiAnalysis?.runs || []),
          ...(anomaly?.runs || []),
          ...(predictive?.runs || []),
        ].sort((a: any, b: any) => {
          const dateA = new Date(a.createdAt || a.metadata?.generatedAt || 0).getTime();
          const dateB = new Date(b.createdAt || b.metadata?.generatedAt || 0).getTime();
          return dateB - dateA;
        });
        
        return {
          runs: allRuns,
          total: allRuns.length,
          count: allRuns.length,
        };
      }
      
      // For specific report types, fetch that type only
      const type = selectedReportType === "comprehensive" ? "ai-analysis" :
                   selectedReportType === "anomaly" ? "anomaly-detection" :
                   "predictive-analytics";
      const result = await getReportRuns({ 
        days, 
        type,
        limit: 50 
      });
      
      console.log("Report runs data:", result);
      console.log("Report runs count:", result?.runs?.length || 0);
      if (result?.runs?.length > 0) {
        console.log("Sample report type:", result.runs[0].reportType);
      }
      return result;
    },
  });

  // Generate new AI report
  const generateReportMutation = useMutation({
    mutationFn: async (reportType: "ai-analysis" | "anomaly-detection" | "predictive-analytics") => {
      const days = timeRangeToDays(selectedTimeRange);
      const to = new Date();
      const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
      
      return generateReport({
        reportType,
        period: { from, to },
        title: `${reportType} report`,
        save: true,
      });
    },
    onSuccess: () => {
      refetchRuns();
      toast({
        title: "Report Generated",
        description: "AI report has been generated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Generation Failed",
        description: error.response?.data?.message || error.message || "Failed to generate report",
        variant: "destructive",
      });
    },
  });

  // Export report
  const handleExportReport = async (runId: string, format: "CSV" | "HTML" | "PDF") => {
    setIsExporting(true);
    try {
      if (format === "PDF") {
        // For PDF, we need to get the report data first and generate PDF from it
        const result = await exportReport({
          runId,
          format: "HTML",
        });
        const htmlText = await result.blob.text();
        
        // Find the report run to get structured data for PDF generation
        const reportRun = reportRunsData?.runs?.find((r: any) => (r._id || r.id) === runId);
        if (reportRun) {
          generatePdfFromReportData(reportRun, result.filename.replace('.html', '.pdf'));
        } else {
          // Fallback to HTML to PDF conversion
          const { htmlToPdf } = await import("../../lib/reportUtils");
          htmlToPdf(htmlText, result.filename.replace('.html', '.pdf'));
        }
      } else {
        const result = await exportReport({
          runId,
          format,
        });
        downloadBlob(result.blob, result.filename);
      }
      toast({
        title: "Export Successful",
        description: `Report exported as ${format}`,
      });
    } catch (error: any) {
      console.error("Failed to export report:", error);
      toast({
        title: "Export Failed",
        description: error.response?.data?.message || error.message || "Failed to export report",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Generate new report
  const handleGenerateReport = async () => {
    setIsGenerating(true);
    try {
      const reportType = selectedReportType === "comprehensive" ? "ai-analysis" :
                        selectedReportType === "anomaly" ? "anomaly-detection" :
                        "predictive-analytics";
      await generateReportMutation.mutateAsync(reportType);
    } catch (error) {
      // Error handling is done in mutation
    } finally {
      setIsGenerating(false);
    }
  };

  // Transform backend report runs to frontend format
  const transformReportRun = (run: any) => {
    const reportType = run.reportType || "";
    let type = "comprehensive";
    if (reportType === "anomaly-detection") type = "anomaly";
    else if (reportType === "predictive-analytics") type = "predictive";
    else if (reportType === "ai-analysis") type = "comprehensive";

    // Extract insights from summary
    const insights: any = {};
    if (run.summary) {
      if (run.reportType === "ai-analysis") {
        insights.overallScore = run.summary.averageAIScore || 0;
        insights.anomaliesDetected = run.summary.totalAnomaliesDetected || 0;
        insights.predictionsGenerated = run.summary.stablePredictionsCount || 0;
        insights.recommendationsActive = run.summary.totalRecommendationsGenerated || 0;
        insights.riskLevel = insights.overallScore < 50 ? "High" : insights.overallScore < 75 ? "Medium" : "Low";
      } else if (run.reportType === "anomaly-detection") {
        insights.totalAnomalies = run.summary.totalAnomaliesDetected || 0;
        insights.highSeverity = run.summary.highSeverityAnomaliesCount || 0;
        insights.mediumSeverity = run.summary.mediumSeverityAnomaliesCount || 0;
        insights.lowSeverity = run.summary.lowSeverityAnomaliesCount || 0;
      } else if (run.reportType === "predictive-analytics") {
        insights.predictionsGenerated = run.summary.totalPredictionsGenerated || 0;
        insights.avgConfidence = run.summary.averageConfidenceLevel || 0;
        insights.highRiskPredictions = run.summary.highRiskPredictions || 0;
        insights.mediumRiskPredictions = run.summary.mediumRiskPredictions || 0;
        insights.lowRiskPredictions = run.summary.lowRiskPredictions || 0;
      }
    }

    // Extract key findings and recommendations from core rows
    const keyFindings: string[] = [];
    const recommendations: string[] = [];
    
    if (run.coreRows && run.coreRows.length > 0) {
      run.coreRows.slice(0, 4).forEach((row: any) => {
        const deviceName = row.deviceName || row.device || `Device ${row.deviceId || 'Unknown'}`;
        if (run.reportType === "ai-analysis") {
          if (row.aiScore !== undefined && row.aiScore < 70) {
            keyFindings.push(`${deviceName} has low AI score: ${row.aiScore}`);
          }
          if (row.anomaliesDetectedCount > 0) {
            keyFindings.push(`${deviceName} has ${row.anomaliesDetectedCount} anomalies detected`);
          }
          if (row.recommendationsCount > 0) {
            recommendations.push(`Review ${deviceName} - ${row.recommendationsCount} recommendations available`);
          }
        } else if (run.reportType === "anomaly-detection") {
          if (row.anomalyType && row.severity) {
            keyFindings.push(`${deviceName}: ${row.anomalyType} - ${row.severity} severity`);
          }
          if (row.severity === "High") {
            recommendations.push(`Immediately investigate ${row.anomalyType || 'anomaly'} on ${deviceName}`);
          }
        } else if (run.reportType === "predictive-analytics") {
          if (row.recommendation === "Scale Up" || row.recommendation?.includes("Scale")) {
            const utilization = clampPct(row.predictedUtilizationPct ?? row.predictedValue ?? 0);
            keyFindings.push(`${deviceName} requires scaling - ${utilization}% predicted`);
            recommendations.push(`Scale up ${deviceName} resources`);
          }
        }
      });
    }

    return {
      id: run._id || run.id,
      title: run.reportTitle || `${reportType} report`,
      type,
      generatedAt: run.metadata?.generatedAt || run.createdAt || new Date().toISOString(),
      status: "completed",
      insights,
      summary: run.metadata?.description || run.summary?.description || `AI ${reportType} report generated`,
      keyFindings: keyFindings.length > 0 ? keyFindings : ["No significant findings"],
      recommendations: recommendations.length > 0 ? recommendations : ["Continue monitoring"],
    };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed": return "bg-green-100 text-green-800";
      case "processing": return "bg-yellow-100 text-yellow-800";
      case "failed": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case "High": return "bg-red-100 text-red-800";
      case "Medium": return "bg-yellow-100 text-yellow-800";
      case "Low": return "bg-green-100 text-green-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  // Use real data from backend only - filter to AI reports only (safety check)
  const reports = isLoadingRuns 
    ? [] 
    : (reportRunsData?.runs || [])
        .filter((run: any) => {
          // Safety check: Only include AI report types (should already be filtered by query)
          const reportType = run.reportType || "";
          return AI_REPORT_TYPES.includes(reportType);
        })
        .map(transformReportRun);
  
  const filteredReports = reports.filter((report: any) => {
    if (selectedReportType !== "all" && report.type !== selectedReportType) {
      return false;
    }
    return true;
  });

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-dark flex items-center">
              <Sparkles className="h-6 w-6 text-purple-500 mr-3" />
              AI Reports
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Comprehensive AI-generated reports and analysis for your network infrastructure
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center space-x-3">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => refetchRuns()}
              disabled={isLoadingRuns}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${isLoadingRuns ? 'animate-spin' : ''}`} />
              {isLoadingRuns ? "Loading..." : "Refresh Reports"}
            </Button>
            <Button 
              size="sm"
              onClick={handleGenerateReport}
              disabled={isGenerating || selectedReportType === "all"}
            >
              <Plus className="h-4 w-4 mr-1" />
              {isGenerating ? "Generating..." : "Generate Report"}
            </Button>
          </div>
        </div>
      </div>

      {/* Error State */}
      {runsError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Failed to load reports</h3>
              <p className="text-sm text-red-600 mt-1">
                {(runsError as any)?.response?.data?.message || (runsError as any)?.message || "Please try refreshing the page."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Report Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Reports</p>
                <p className="text-2xl font-bold text-gray-900">{filteredReports.length}</p>
              </div>
              <FileText className="h-6 w-6 text-gray-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Completed</p>
                <p className="text-2xl font-bold text-green-500">
                  {filteredReports.filter((r: any) => r.status === "completed").length}
                </p>
              </div>
              <CheckCircle2 className="h-6 w-6 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Processing</p>
                <p className="text-2xl font-bold text-yellow-500">
                  {filteredReports.filter((r: any) => r.status === "processing").length}
                </p>
              </div>
              <Clock className="h-6 w-6 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Avg Score</p>
                <p className="text-2xl font-bold text-blue-500">
                  {filteredReports.length > 0 
                    ? Math.round(
                        filteredReports
                          .filter((r: any) => r.insights.overallScore !== undefined)
                          .reduce((sum: number, r: any) => sum + (r.insights.overallScore || 0), 0) /
                        Math.max(1, filteredReports.filter((r: any) => r.insights.overallScore !== undefined).length)
                      )
                    : 0}
                </p>
              </div>
              <Brain className="h-6 w-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Report Filters */}
      <div className="mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
                  <select
                  value={selectedReportType}
                  onChange={(e) => setSelectedReportType(e.target.value as typeof selectedReportType)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="all">All Reports</option>
                  <option value="comprehensive">Comprehensive Analysis</option>
                  <option value="anomaly">Anomaly Detection</option>
                  <option value="predictive">Predictive Analytics</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time Range</label>
                <select
                  value={selectedTimeRange}
                  onChange={(e) => setSelectedTimeRange(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="90d">Last 90 Days</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reports List */}
      {isLoadingRuns ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading reports...</span>
          </div>
        </div>
      ) : filteredReports.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="h-12 w-12 text-gray-400 mb-4 mx-auto" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Reports Available</h3>
            <p className="text-sm text-gray-500 mb-4">
              {selectedReportType === "all" 
                ? "No reports have been generated yet. Generate a new report to get started."
                : `No ${selectedReportType} reports found for the selected time range.`}
            </p>
            <Button 
              onClick={handleGenerateReport}
              disabled={isGenerating || selectedReportType === "all"}
            >
              <Plus className="h-4 w-4 mr-1" />
              Generate Report
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredReports.map((report: any) => (
            <Card key={report.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{report.title}</h3>
                      <Badge className={getStatusColor(report.status)}>
                        {report.status}
                      </Badge>
                      {report.insights.riskLevel && (
                        <Badge className={getRiskColor(report.insights.riskLevel)}>
                          {report.insights.riskLevel} Risk
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{report.summary}</p>
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>Generated: {new Date(report.generatedAt).toLocaleString()}</span>
                      <span>Type: {report.type}</span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleExportReport(report.id, "HTML")}
                      disabled={isExporting}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleExportReport(report.id, "CSV")}
                      disabled={isExporting}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      CSV
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleExportReport(report.id, "PDF")}
                      disabled={isExporting}
                    >
                      <File className="h-4 w-4 mr-1" />
                      PDF
                    </Button>
                  </div>
                </div>

                {/* Report Insights */}
                {Object.keys(report.insights).length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    {Object.entries(report.insights).map(([key, value]: [string, any]) => {
                      // Skip riskLevel as it's shown as a badge
                      if (key === "riskLevel") return null;
                      return (
                        <div key={key} className="bg-gray-50 p-3 rounded-lg">
                          <div className="text-xs text-gray-500 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </div>
                          <div className="text-lg font-semibold text-gray-900">
                            {typeof value === 'number' ? value : String(value)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Key Findings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Key Findings</h4>
                    <ul className="space-y-1">
                      {report.keyFindings.slice(0, 3).map((finding: string, index: number) => (
                        <li key={index} className="text-sm text-gray-600 flex items-start">
                          <CheckCircle2 className="h-3 w-3 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                          {finding}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Recommendations</h4>
                    <ul className="space-y-1">
                      {report.recommendations.slice(0, 3).map((recommendation: string, index: number) => (
                        <li key={index} className="text-sm text-gray-600 flex items-start">
                          <Lightbulb className="h-3 w-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          {recommendation}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
